// The tensor app's API (design 02, 1st stage; api/openapi.yaml, tag tensor).
// From tensor.housemouse.dev only sign-in (/api/auth/*), /api/users and
// /api/tensor/* reach the server; everything else is talk's.
import { request } from '../api/client'
import type { components } from '../api/generated/schema'

type S = components['schemas']

export type Id = S['Id']
export type TensorRole = S['TensorRole']
export type TensorPerson = S['TensorPerson']
export type TensorBootstrap = S['TensorBootstrap']
export type TensorWorkbook = S['TensorWorkbook']
export type TensorWorkbookFull = S['TensorWorkbookFull']
export type TensorItem = S['TensorItem']
export type TensorRow = S['TensorRow']
export type TensorRowInput = S['TensorRowInput']
export type TensorRowCreate = S['TensorRowCreate']
export type TensorBudget = S['TensorBudget']
export type TensorChange = S['TensorChange']
export type TensorSync = S['TensorSync']
export type TensorSheet = S['TensorSheet']
export type TensorSheetInput = S['TensorSheetInput']
export type TensorStyle = S['TensorStyle']
export type TensorCell = S['TensorCell']
export type TensorCellInput = S['TensorCellInput']
export type TensorCellsPage = S['TensorCellsPage']
export type TensorCellsChange = S['TensorCellsChange']
export type TensorStructureChange = S['TensorStructureChange']
export type TensorName = S['TensorName']
export type TensorDim = S['TensorDim']
export type TensorDimInput = S['TensorDimInput']
export type TensorDef = S['TensorDef']
export type TensorDefInput = S['TensorDefInput']
export type TensorDimKey = S['TensorDimKey']
export type TensorEntry = S['TensorEntry']
export type TensorEntryInput = S['TensorEntryInput']
export type TensorEntriesPage = S['TensorEntriesPage']
export type TensorSavedView = S['TensorSavedView']
export type TensorViewConfig = S['TensorViewConfig']
/** The built-in dimensions' roles (사람, 분류, 결제수단); 'custom' is a dimension people made. */
export type BuiltinRole = Exclude<TensorRole, 'custom'>
export type Visibility = TensorWorkbook['visibility']
export type RowKind = TensorRow['kind']

/** What /ws/tensor sends (hello, change, pong, resync, bye). */
export type TensorWsMessage =
  | S['WsHello']
  | S['WsTensorChange']
  | S['WsPong']
  | S['WsResync']
  | S['WsBye']

const get = <T>(path: string) => request<T>('GET', path)
const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, body)
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body)
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body)
const del = <T>(path: string) => request<T>('DELETE', path)

const W = '/api/tensor/workbooks'

export const tapi = {
  bootstrap: () => get<TensorBootstrap>('/api/tensor/bootstrap'),
  sync: (since: number, limit = 1000) => get<TensorSync>(`/api/tensor/sync?since=${since}&limit=${limit}`),

  createWorkbook: (name: string, visibility: Visibility) =>
    post<{ workbook: TensorWorkbook }>(W, { name, visibility }),
  workbook: (id: Id) => get<TensorWorkbookFull>(`${W}/${id}`),
  updateWorkbook: (id: Id, body: { name?: string; visibility?: Visibility; budget_alerts?: boolean }) =>
    patch<{ workbook: TensorWorkbook }>(`${W}/${id}`, body),
  deleteWorkbook: (id: Id) => del<void>(`${W}/${id}`),

  addItem: (workbookId: Id, role: BuiltinRole, name: string, parentId = 0) =>
    post<{ item: TensorItem }>(`${W}/${workbookId}/items`, { role, name, ...(parentId ? { parent_id: parentId } : {}) }),
  /** An item of a dimension people made (a list, not a range). */
  addDimItem: (workbookId: Id, dimId: Id, name: string) =>
    post<{ item: TensorItem }>(`${W}/${workbookId}/items`, { role: 'custom', dim_id: dimId, name }),
  updateItem: (id: Id, body: { name?: string; parent_id?: number; position?: number; version?: number }) =>
    patch<{ item: TensorItem; stale: boolean }>(`/api/tensor/items/${id}`, body),
  deleteItem: (id: Id) => del<void>(`/api/tensor/items/${id}`),

  /** A page of rows with id > after (at most 5000). */
  rows: (workbookId: Id, after: number, limit = 5000) =>
    get<{ rows: TensorRow[]; next_after: Id | null }>(`${W}/${workbookId}/rows?after=${after}&limit=${limit}`),
  addRow: (workbookId: Id, body: TensorRowCreate) => post<{ row: TensorRow }>(`${W}/${workbookId}/rows`, body),
  addRows: (workbookId: Id, rows: TensorRowCreate[]) =>
    post<{ rows: TensorRow[]; created: number }>(`${W}/${workbookId}/rows/batch`, { rows }),
  updateRow: (id: Id, body: TensorRowInput) => patch<{ row: TensorRow; stale: boolean }>(`/api/tensor/rows/${id}`, body),
  deleteRow: (id: Id) => del<void>(`/api/tensor/rows/${id}`),
  csvUrl: (workbookId: Id) => `${W}/${workbookId}/rows.csv`,

  setBudget: (workbookId: Id, month: string, categoryItem: Id, amount: number) =>
    put<{ budget: TensorBudget }>(`${W}/${workbookId}/budgets`, { month, category_item: categoryItem, amount }),
  copyBudgets: (workbookId: Id, from: string, to: string) =>
    post<{ budgets: TensorBudget[] }>(`${W}/${workbookId}/budgets/copy`, { from, to }),

  // ---- 표: grid sheets, cells and defined names (2nd stage, design 02 12.1) ----

  addSheet: (workbookId: Id, name: string) => post<{ sheet: TensorSheet }>(`${W}/${workbookId}/sheets`, { name }),
  updateSheet: (id: Id, body: TensorSheetInput) => patch<{ sheet: TensorSheet }>(`/api/tensor/sheets/${id}`, body),
  deleteSheet: (id: Id) => del<void>(`/api/tensor/sheets/${id}`),
  /** A page of cells after (row, col) in row order (-1, -1: from the start). */
  cells: (sheetId: Id, afterRow = -1, afterCol = -1, limit = 10000) =>
    get<TensorCellsPage>(`/api/tensor/sheets/${sheetId}/cells?after_row=${afterRow}&after_col=${afterCol}&limit=${limit}`),
  /** At most 5000 cells; 409 stale when the sheet's rows or columns changed since structureVersion. */
  setCells: (sheetId: Id, structureVersion: number, cells: TensorCellInput[]) =>
    put<{ structure_version: number; cells: TensorCell[] }>(`/api/tensor/sheets/${sheetId}/cells`, {
      structure_version: structureVersion,
      cells,
    }),
  /** Inserts (count > 0) or deletes (count < 0) rows or columns. */
  structure: (sheetId: Id, structureVersion: number, axis: 'row' | 'col', at: number, count: number) =>
    post<{ sheet: TensorSheet }>(`/api/tensor/sheets/${sheetId}/structure`, {
      structure_version: structureVersion,
      axis,
      at,
      count,
    }),
  addName: (workbookId: Id, name: string, formula: string) =>
    post<{ name: TensorName }>(`${W}/${workbookId}/names`, { name, formula }),
  updateName: (id: Id, body: { name?: string; formula?: string }) =>
    patch<{ name: TensorName }>(`/api/tensor/names/${id}`, body),
  deleteName: (id: Id) => del<void>(`/api/tensor/names/${id}`),

  // ---- 차원: dimensions people make (design 02 3.2) ----

  addDim: (workbookId: Id, body: TensorDimInput & { name: string; kind: TensorDim['kind'] }) =>
    post<{ dim: TensorDim }>(`${W}/${workbookId}/dims`, body),
  updateDim: (id: Id, body: TensorDimInput) => patch<{ dim: TensorDim; stale: boolean }>(`/api/tensor/dims/${id}`, body),
  deleteDim: (id: Id) => del<void>(`/api/tensor/dims/${id}`),

  // ---- 텐서: input tensors (design 02 3.1) ----

  addTensor: (workbookId: Id, body: TensorDefInput & { name: string; dims: TensorDimKey[] }) =>
    post<{ tensor: TensorDef }>(`${W}/${workbookId}/tensors`, body),
  updateTensor: (id: Id, body: TensorDefInput) => patch<{ tensor: TensorDef; stale: boolean }>(`/api/tensor/tensors/${id}`, body),
  deleteTensor: (id: Id) => del<void>(`/api/tensor/tensors/${id}`),
  /** A page of a tensor's cells with coord > after (at most 10000). */
  tensorCells: (id: Id, after = '', limit = 10000) =>
    get<TensorEntriesPage>(`/api/tensor/tensors/${id}/cells?after=${encodeURIComponent(after)}&limit=${limit}`),
  /** At most 5000 cells, all or none; value null clears. */
  setTensorCells: (id: Id, cells: TensorEntryInput[]) =>
    put<{ cells: TensorEntry[]; stale: boolean }>(`/api/tensor/tensors/${id}/cells`, { cells }),

  // ---- 저장한 보기: saved views (design 02 3.6) ----

  addView: (workbookId: Id, name: string, config: TensorViewConfig) =>
    post<{ view: TensorSavedView }>(`${W}/${workbookId}/views`, { name, config }),
  updateView: (id: Id, body: { name?: string; config?: TensorViewConfig; version?: number }) =>
    patch<{ view: TensorSavedView; stale: boolean }>(`/api/tensor/views/${id}`, body),
  deleteView: (id: Id) => del<void>(`/api/tensor/views/${id}`),
}
